import React from "react";
import { styled } from "styled-components";
import { SchemaField } from "./configTypes";
import { DraftValue } from "./configModel";
import SegmentedControl, { SegmentedOption } from "./SegmentedControl";
import { glyphChar, severityStyle } from "./severity";
import { listEntryBase } from "./ValueChip";
import { tokens } from "./tokens";

/**
 * The input for one config field, chosen by the schema's `type`.
 *
 * Every enum renders as a closed list built from the served options, so a value
 * outside the vocabulary cannot be typed in the first place — the reason the
 * source spreadsheet's vocabulary drifted across tabs is that it accepted
 * anything. The blank option is always offered: clearing a field is a legal
 * edit (it becomes a DynamoDB REMOVE), and refusing to offer it would strand
 * a value someone set by mistake.
 *
 * Short enums — up to four options, which is every approval and every roster
 * field — render as a segmented control painted with each option's severity
 * and glyph, so the choice on the form looks like the value in the record. A
 * `<select>` strips that away, and turns a one-click "Yes / No / N/A" into
 * open-scan-click. Longer vocabularies (allocation, run type) stay a select.
 */
interface FieldControlProps {
  field: SchemaField;
  value: DraftValue;
  error?: string;
  onChange: (value: DraftValue) => void;
  /** Validate once the operator has left the control. */
  onBlur?: () => void;
  /** id of the visible label, for controls that are not a single input. */
  labelledBy?: string;
  /** id(s) of the error or help text below the control. */
  describedBy?: string;
  required?: boolean;
  /**
   * Option value -> why a cross-field rule refuses it on the row as the draft
   * stands (`blockedOptions`). A blocked option renders disabled with the
   * reason, instead of being accepted here and refused by the API.
   */
  blocked?: Record<string, string>;
}

/** Enums with this many options or fewer render as segments, absent a choice. */
export const SEGMENT_MAX_OPTIONS = 4;

/**
 * The options a control offers: everything not retired, plus the current value
 * even if it has been — a row holding a retired value must stay saveable
 * unchanged, and a control that silently dropped it would read as a change.
 */
export const offeredOptions = (field: SchemaField, current: string) =>
  (field.options ?? []).filter((o) => !o.retired || o.value === current);

/**
 * Chiclets or a dropdown. The schema's explicit `control` wins (fleet-admins
 * set it in the Schema editor); otherwise the rule of thumb — four options or
 * fewer read as chiclets — counted over the options actually offered.
 */
export const isSegmented = (field: SchemaField, current = ""): boolean =>
  field.type === "enum" &&
  (field.control
    ? field.control === "segmented"
    : offeredOptions(field, current).length <= SEGMENT_MAX_OPTIONS);

const todayIso = () => new Date().toISOString().slice(0, 10);

const FieldControl: React.FC<FieldControlProps> = ({
  field,
  value,
  error,
  onChange,
  onBlur,
  labelledBy,
  describedBy,
  required,
  blocked,
}) => {
  const id = `config-${field.name}`;
  const invalid = Boolean(error);
  const text = Array.isArray(value) ? value.join("\n") : value;
  const aria = {
    "aria-invalid": invalid || undefined,
    "aria-describedby": describedBy,
    "aria-required": required || undefined,
  };

  if (field.type === "enum") {
    const current = typeof text === "string" ? text : "";
    const options = offeredOptions(field, current);
    if (isSegmented(field, current)) {
      const segments: SegmentedOption<string>[] = [
        { value: "", label: "Not set", tone: severityStyle("neutral") },
        ...options.map((option) => ({
          value: option.value,
          label: option.label,
          glyph: option.glyph ? glyphChar(option.glyph) : undefined,
          tone: severityStyle(option.severity),
          help: option.help,
          disabled: Boolean(blocked?.[option.value]),
          disabledReason: blocked?.[option.value],
        })),
      ];
      return (
        <SegmentedControl
          id={id}
          role="radiogroup"
          options={segments}
          value={typeof text === "string" ? text : ""}
          onChange={onChange}
          onBlur={onBlur}
          ariaLabelledBy={labelledBy}
          ariaDescribedBy={describedBy}
          invalid={invalid}
          fill
        />
      );
    }
    return (
      <Select
        id={id}
        $invalid={invalid}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        {...aria}
      >
        <option value="">— not set —</option>
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={Boolean(blocked?.[option.value])}
          >
            {blocked?.[option.value]
              ? `${option.label} — ${blocked[option.value]}`
              : option.label}
          </option>
        ))}
      </Select>
    );
  }

  if (field.type === "string_list") {
    return (
      <ListField
        id={id}
        invalid={invalid}
        entries={Array.isArray(value) ? value : []}
        maxLength={field.max_length}
        onChange={onChange}
        onBlur={onBlur}
        describedBy={describedBy}
      />
    );
  }

  if (field.type === "text") {
    const length = typeof text === "string" ? text.length : 0;
    const max = field.max_length;
    // The cap is enforced by the control, which is fine — but a box that just
    // stops accepting input with no explanation is not. Say how close it is,
    // once it is close enough to matter.
    const nearLimit = max !== undefined && length >= max * 0.8;
    return (
      <>
        <TextArea
          id={id}
          $invalid={invalid}
          rows={5}
          value={text}
          maxLength={max}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          {...aria}
        />
        {nearLimit && (
          <Counter aria-live="polite" $full={length >= (max ?? Infinity)}>
            {length.toLocaleString()} / {max?.toLocaleString()} characters
          </Counter>
        )}
      </>
    );
  }

  const inputType =
    field.type === "date" ? "date" : field.type === "int" ? "number" : "text";

  return (
    <Input
      id={id}
      $invalid={invalid}
      type={inputType}
      value={text}
      step={field.type === "int" ? 1 : undefined}
      maxLength={field.type === "int" ? undefined : field.max_length}
      // A field whose whole point is recency cannot have happened yet; a typo
      // that lands in 2062 would otherwise read as fresh forever.
      max={
        field.type === "date" && field.stale_after_days ? todayIso() : undefined
      }
      placeholder={field.type === "url" ? "https://…" : undefined}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      {...aria}
    />
  );
};

/**
 * A list field as a list: one removable entry per item, plus a box to add one.
 *
 * This was a textarea with "one entry per line", which asks the reader to hold
 * a convention in their head and hides the actual shape of the value — whether
 * a trailing blank line counts, whether a wrapped line is one entry or two.
 * Each entry is now its own object on screen, so what you see is exactly what
 * the array contains.
 *
 * The draft stays the canonical array throughout, which is what keeps the diff
 * honest: `buildChanges` compares arrays element-wise, and a control that only
 * produced an array on blur would show a field as clean while it wasn't.
 *
 * Enter commits the pending entry. Backspace on an empty box pulls the last one
 * back for editing rather than deleting it outright — a destructive keystroke
 * you can hit by accident should undo, not discard.
 */
const ListField: React.FC<{
  id: string;
  invalid: boolean;
  entries: string[];
  maxLength?: number;
  onChange: (value: DraftValue) => void;
  onBlur?: () => void;
  describedBy?: string;
}> = ({ id, invalid, entries, maxLength, onChange, onBlur, describedBy }) => {
  const [pending, setPending] = React.useState("");

  const commit = (raw: string) => {
    const entry = raw.trim();
    if (!entry) return;
    onChange([...entries, entry]);
    setPending("");
  };

  const removeAt = (index: number) =>
    onChange(entries.filter((_, i) => i !== index));

  return (
    <ListWrap $invalid={invalid}>
      {entries.length > 0 && (
        <Entries aria-label="Entries">
          {entries.map((entry, index) => (
            <Entry key={`${entry}-${index}`}>
              <EntryText title={entry}>{entry}</EntryText>
              <RemoveEntry
                type="button"
                aria-label={`Remove ${entry}`}
                onClick={() => removeAt(index)}
              >
                ×
              </RemoveEntry>
            </Entry>
          ))}
        </Entries>
      )}
      <EntryInput
        id={id}
        value={pending}
        maxLength={maxLength}
        placeholder={entries.length ? "Add another…" : "Add an entry…"}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(e) => setPending(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            // Never submit a form or bubble to a parent's Enter handling.
            e.preventDefault();
            commit(pending);
          } else if (e.key === "Backspace" && !pending && entries.length) {
            e.preventDefault();
            setPending(entries[entries.length - 1]);
            removeAt(entries.length - 1);
          }
        }}
        // A typed-but-uncommitted entry is one the operator believes they
        // added. Committing on blur means it survives clicking straight to
        // Review instead of vanishing with the focus.
        onBlur={() => {
          commit(pending);
          onBlur?.();
        }}
      />
    </ListWrap>
  );
};

const ListWrap = styled.div<{ $invalid: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
  background: ${tokens.white};
  border: ${(p) => borderFor(p.$invalid)};
  border-radius: 8px;
  padding: 6px;

  &:focus-within {
    border-color: ${tokens.blue};
    box-shadow: 0 0 0 3px rgba(0, 105, 255, 0.12);
  }
`;

const Entries = styled.ul`
  display: contents;
  margin: 0;
  padding: 0;
  list-style: none;
`;

// The same pill the read view draws (ValueChip's list entries) — this variant
// only adds the remove affordance, so entering Edit restyles nothing.
const Entry = styled.li`
  ${listEntryBase}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 4px 3px 9px;
`;

const EntryText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveEntry = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex: none;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: none;
  color: ${tokens.slate};
  font-size: 15px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: rgba(229, 72, 77, 0.12);
    color: ${tokens.groundedText};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 1px;
  }
`;

const EntryInput = styled.input`
  flex: 1;
  min-width: 120px;
  border: none;
  outline: none;
  background: none;
  font-family: inherit;
  font-size: 13.5px;
  color: ${tokens.ink};
  padding: 3px 4px;

  &::placeholder {
    color: ${tokens.slate};
  }
`;

const base = `
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
  font-size: 13.5px;
  color: ${tokens.ink};
  background: ${tokens.white};
  border-radius: 8px;
  padding: 8px 10px;
  transition: border-color 0.12s;

  &:focus {
    outline: none;
    border-color: ${tokens.blue};
    box-shadow: 0 0 0 3px rgba(0, 105, 255, 0.12);
  }

  &:disabled {
    background: ${tokens.offWhite};
    color: ${tokens.slate};
  }
`;

// The unfocused boundary has to be findable on a white card — the hairline
// `line` grey reads at 1.25:1 and left the fields invisible until clicked.
const borderFor = (invalid: boolean) =>
  `1px solid ${invalid ? tokens.groundedDot : tokens.inputLine}`;

const Input = styled.input<{ $invalid: boolean }>`
  ${base}
  border: ${(p) => borderFor(p.$invalid)};
`;

const TextArea = styled.textarea<{ $invalid: boolean }>`
  ${base}
  border: ${(p) => borderFor(p.$invalid)};
  resize: vertical;
  line-height: 1.45;
`;

const Counter = styled.div<{ $full: boolean }>`
  margin-top: 4px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  text-align: right;
  color: ${(p) => (p.$full ? tokens.majorText : tokens.slate)};
`;

const Select = styled.select<{ $invalid: boolean }>`
  ${base}
  border: ${(p) => borderFor(p.$invalid)};
  cursor: pointer;
`;

export default FieldControl;
